/**
 * @file site-access-view.ts
 * @description Что показать про паузу сайта (phase19.16, SB138;
 * uiux-site-closed.md §4, §7, §8; db-design-site-closed.md §6): действующая
 * пауза, модель плашки кабинета, строка истории. Чистые функции от
 * состояния сервера и «сейчас» по часам сервера — без React.
 *
 * Срок проверяется здесь же (`isSiteClosureActive`): плашка уходит ровно в
 * `autoOpenAt`, не дожидаясь следующего опроса. Платформа сильнее магазина.
 * phase19.28 (PL8): пауза «запуск» (`launchClosure`) — магазин ещё не открыт;
 * сильнее паузы магазина, слабее паузы платформы.
 */

import {
  SITE_CLOSURE_LAUNCH_CABINET_HINT,
  SITE_CLOSURE_LONG_PAUSE_DAYS,
  SITE_CLOSURE_REMIND_BEFORE_SEC,
  isSiteClosureActive,
  type SiteAccessStateDto,
  type SiteClosureDto,
  type SiteClosureHistoryItemDto,
  type SiteClosureOpenedVia,
} from "@chaiberi/shared";

import { formatPauseDuration, zonedClock, zonedDayIso, zonedDayMonth, zonedSince, zonedWhen } from "./site-access-time";

/** Действует ли пауза сейчас (по часам сервера). */
function active(closure: SiteClosureDto | null, now: Date): closure is SiteClosureDto {
  return closure !== null && isSiteClosureActive({ openedAt: null, autoOpenAt: closure.autoOpenAt }, now);
}

/** Действующая пауза: платформы, затем «запуск», затем магазина; `null` — сайт открыт. */
export function activeSiteClosure(state: SiteAccessStateDto | null, now: Date): SiteClosureDto | null {
  if (!state) return null;
  if (active(state.platformClosure, now)) return state.platformClosure;
  const launch = state.launchClosure ?? null;
  if (active(launch, now)) return launch;
  if (active(state.shopClosure, now)) return state.shopClosure;
  return null;
}

/** Плашка кабинета, пока магазин ещё не открыт (пауза «запуск», PL8). */
export interface LaunchBannerModel {
  kind: "launch";
  closureId: string;
  text: string;
  canManage: boolean;
}

/** Плашка кабинета при паузе платформы. */
export interface PlatformBannerModel {
  kind: "platform";
  text: string;
}

/** Плашка кабинета при паузе магазина. */
export interface ShopBannerModel {
  kind: "shop";
  closureId: string;
  /** «14:20» или «12.10 14:20». */
  since: string;
  /** Кто закрыл; `null` — сотрудник удалён. */
  by: string | null;
  /** «42 мин». */
  elapsed: string;
  /** «в 15:20», «завтра в 01:20»; `null` — без срока. */
  autoOpen: string | null;
  /** До срока ≤ 10 мин — минуты (не меньше 1), иначе `null` (SB138: «Продлить на 1 ч»). */
  soonMinutes: number | null;
  /** Без срока дольше 3 суток — предупреждение о поиске. */
  longPause: boolean;
  canManage: boolean;
}

export type SiteClosedBannerModel = PlatformBannerModel | LaunchBannerModel | ShopBannerModel;

/** Текст плашки при паузе платформы (uiux §7). */
export function platformBannerText(closure: SiteClosureDto, now: Date, zone: string): string {
  const reason = closure.platformReason?.trim();
  return `Сайт закрыт платформой Commora с ${zonedSince(new Date(closure.closedAt), now, zone)}${reason ? `: ${reason}` : ""}. Открыть может только платформа.`;
}

/** Модель плашки «Сайт закрыт»; `null` — сайт открыт, плашки нет. */
export function siteClosedBanner(state: SiteAccessStateDto | null, now: Date): SiteClosedBannerModel | null {
  const closure = activeSiteClosure(state, now);
  if (!state || !closure) return null;
  const zone = state.timeZone;
  if (closure.source === "platform") return { kind: "platform", text: platformBannerText(closure, now, zone) };
  if (closure.source === "launch") {
    return { kind: "launch", closureId: closure.id, text: SITE_CLOSURE_LAUNCH_CABINET_HINT, canManage: state.canManage };
  }
  const closedAt = new Date(closure.closedAt);
  const auto = closure.autoOpenAt ? new Date(closure.autoOpenAt) : null;
  const leftSec = auto ? (auto.getTime() - now.getTime()) / 1000 : null;
  return {
    kind: "shop",
    closureId: closure.id,
    since: zonedSince(closedAt, now, zone),
    by: closure.closedBy?.name ?? null,
    elapsed: formatPauseDuration(now.getTime() - closedAt.getTime()),
    autoOpen: auto ? zonedWhen(auto, now, zone) : null,
    soonMinutes: leftSec !== null && leftSec <= SITE_CLOSURE_REMIND_BEFORE_SEC ? Math.max(1, Math.ceil(leftSec / 60)) : null,
    longPause: !auto && now.getTime() - closedAt.getTime() >= SITE_CLOSURE_LONG_PAUSE_DAYS * 86_400_000,
    canManage: state.canManage,
  };
}

/** Как открыли — словами (реестр, а не условие). */
export const SITE_CLOSURE_OPENED_VIA_LABEL: Readonly<Record<SiteClosureOpenedVia, string>> = {
  manual: "вручную",
  schedule: "по сроку",
  platform: "платформой",
};

/** Строка истории: «12.10 14:20–15:05», «Иван», «вручную». */
export interface SiteClosureHistoryRow {
  id: string;
  period: string;
  who: string;
  how: string;
  /** Пауза ещё действует. */
  current: boolean;
}

/** Кто закрыл — по источнику паузы; у паузы магазина — сотрудник (реестр, а не условие). */
const CLOSURE_SOURCE_WHO: Readonly<Partial<Record<SiteClosureHistoryItemDto["source"], string>>> = {
  platform: "Платформа Commora",
  // phase19.28 (PL8): пауза «запуск» ставится при создании магазина, а не сотрудником.
  launch: "Запуск магазина (Commora)",
};

/** Строка истории пауз по поясу магазина. */
export function siteClosureHistoryRow(item: SiteClosureHistoryItemDto, zone: string): SiteClosureHistoryRow {
  const closed = new Date(item.closedAt);
  const opened = item.openedAt ? new Date(item.openedAt) : null;
  const start = `${zonedDayMonth(closed, zone)} ${zonedClock(closed, zone)}`;
  // Открыли в тот же день — только время, иначе с датой.
  const end = opened
    ? zonedDayIso(opened, zone) === zonedDayIso(closed, zone)
      ? zonedClock(opened, zone)
      : `${zonedDayMonth(opened, zone)} ${zonedClock(opened, zone)}`
    : "…";
  const who = CLOSURE_SOURCE_WHO[item.source] ?? item.closedBy?.name ?? "Сотрудник удалён";
  return {
    id: item.id,
    period: `${start}–${end}`,
    who,
    how: item.openedVia ? SITE_CLOSURE_OPENED_VIA_LABEL[item.openedVia] : "закрыт сейчас",
    current: item.openedVia === null,
  };
}
